<template>
  <div>
    <div v-if="isLoading" class="w-full relative" style="height: 80px;">
      <b-loading :is-full-page="false" :active="true" :can-cancel="false" />
    </div>

    <div v-else-if="!chapters.length" class="has-text-centered w-full mt-4">{{ $t('no_results') }}</div>

    <!-- Una grafica por capitulo -->
    <div class="w-full mt-4" v-for="chapter in charts" :key="'tag_scurve_' + chapter.group.id">
      <div v-if="chapter.units.length > 1" class="text-xs text-right pr-2" style="color: orange;">
        {{ $t('s_curve_mixed_units', [chapter.units.join(', ')]) }}
      </div>
      <group-progress-chart
        :group="chapter.group"
        :level="0"
        :main-chart-colors="chartColors"
        :show-title-prefix="false"
        suffix=""
        :max="null"
        :table-main-title="$t('s_curve_cumulative')"
        :table-extra-title="$t('s_curve_month')"
        :table-extra-rows="chapter.monthlyRows"
        :highlight-month="chapter.currentMonth"
        :main-chart-library="chartLibrary"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import Chart from 'chart.js'
import GroupProgressChart from './group-progress-chart.vue'

// Prog late rellena hasta prog early: banda de planificacion
const SERIES = [
  { key: 'prog_early', label: 's_curve_prog_early', color: '#1f2937', dataset: { borderWidth: 2 } },
  { key: 'prog_late', label: 's_curve_prog_late', color: '#d12411', dataset: { borderWidth: 2, fill: '-1', backgroundColor: 'rgba(85, 119, 187, 0.12)' } },
  { key: 'actual', label: 's_curve_actual', color: '#284ec0', dataset: { borderWidth: 3 } },
]

const formatThousands = (value) => (Math.round(value) || 0).toLocaleString('de-DE')

// Linea del mes actual, solo si la grafica trae currentMonthLine
Chart.plugins.register({
  id: 'currentMonthLine',
  afterDatasetsDraw(chart) {
    const date = chart.options.currentMonthLine
    const xScale = chart.scales['x-axis-0']
    const yScale = chart.scales['y-axis-0']
    if (!date || !xScale || !yScale) return
    const x = xScale.getPixelForValue(moment(date).valueOf())
    if (x < xScale.left || x > xScale.right) return
    const ctx = chart.ctx
    ctx.save()
    ctx.strokeStyle = '#e0a800'
    ctx.lineWidth = 1.5
    ctx.setLineDash([5, 4])
    ctx.beginPath()
    ctx.moveTo(x, yScale.top)
    ctx.lineTo(x, yScale.bottom)
    ctx.stroke()
    ctx.restore()
  },
})

export default {
  name: 'dashboard-tags-scurve',

  components: {
    GroupProgressChart,
  },

  props: {
    projectId: { type: Number, required: true },
  },

  data() {
    return {
      chapters: [],
      isLoading: true,
      chartColors: SERIES.map((serie) => serie.color),
      chartLibrary: {
        spanGaps: true,
        currentMonthLine: moment().endOf('month').format('YYYY-MM-DD'),
        elements: {
          point: { radius: 3, hoverRadius: 6, hitRadius: 8, borderWidth: 0 },
          line: { cubicInterpolationMode: 'monotone', fill: false },
        },
        legend: { labels: { usePointStyle: true, boxWidth: 8, padding: 16 } },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: (item, data) => ` ${data.datasets[item.datasetIndex].label}: ${formatThousands(item.yLabel)}`,
          },
        },
        hover: { mode: 'index', intersect: false },
        scales: {
          xAxes: [{ gridLines: { display: false } }],
          yAxes: [{ gridLines: { color: '#edf0f4', drawBorder: false }, ticks: { callback: formatThousands } }],
        },
      },
      observer: null,
    }
  },

  computed: {
    // Series { fin de mes => valor } para group-progress-chart
    charts() {
      return this.chapters.map((chapter) => {
        const dates = chapter.months.map((month) => moment(month, 'YYYY-MM').endOf('month').format('YYYY-MM-DD'))
        const toData = (values) => Object.fromEntries(
          dates.map((date, i) => [date, values[i]]).filter(([, value]) => value !== null)
        )
        const unit = chapter.units.length === 1 ? ` (${chapter.units[0]})` : ''

        return {
          units: chapter.units,
          currentMonth: moment(chapter.current_month, 'YYYY-MM').endOf('month').format('YYYY-MM-DD'),
          group: {
            id: chapter.id,
            name: [chapter.code, chapter.name].filter(Boolean).join(' ') + unit,
            data: SERIES.map((serie) => ({ name: this.$t(serie.label), data: toData(chapter[serie.key].cumulative), dataset: serie.dataset })),
          },
          monthlyRows: SERIES.map((serie) => ({ name: this.$t(serie.label), color: serie.color, data: toData(chapter[serie.key].monthly) })),
        }
      })
    },
  },

  mounted() {
    // Carga al mostrar la pestaña por primera vez
    if (!('IntersectionObserver' in window)) {
      this.getChapters()
      return
    }
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.disconnectObserver()
        this.getChapters()
      }
    })
    this.observer.observe(this.$el)
  },

  beforeDestroy() {
    this.disconnectObserver()
  },

  methods: {
    disconnectObserver() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
    },

    async getChapters() {
      this.isLoading = true
      try {
        const { data } = await axios.get(`/api/v2/project/${this.projectId}/dashboard/tagscurve`, {
          params: { location_id: 0, status_id: 1 },
        })
        if (data && data.success) {
          this.chapters = data.data
        } else {
          this.$notify.error('error_loading_data')
        }
      } catch (e) {
        this.$notify.error('error_loading_data')
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
